import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Fund from '@/models/Fund';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import { parseSupportedsExcel, type ParseResult } from './shared/excelParser';
import { loadBankDirectory } from './shared/bankResolution';
import type {
  BulkSupportedRow,
  BulkCreateResult,
  BulkCreateSupportedsPayload,
  UploadStep,
} from './shared/types';
import FileUploadArea from './shared/FileUploadArea';
import ConfirmDialog from './shared/ConfirmDialog';
import ResultsTable from './shared/ResultsTable';
import styles from './shared/SharedStyles.module.css';

interface UploadSupportedsTabProps {
  targetFund: Fund | null;
  /** Defaults to the management-area (Admin) endpoints; the fund screen passes its own, fund-scoped ones. */
  postUrl?: string;
  templateUrl?: string;
  /** Refuse a file with more rows than the endpoint accepts, before anything is sent. */
  maxRows?: number;
  /** Called once the server has answered, so the screen behind can refresh. */
  onUploaded?: () => void;
}

export default function UploadSupportedsTab({
  targetFund,
  postUrl = 'management/bulk-uploads/supporteds',
  templateUrl = 'management/bulk-uploads/templates/supporteds',
  maxRows,
  onUploaded,
}: UploadSupportedsTabProps) {
  const { t } = useTranslation();

  const [step, setStep] = useState<UploadStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult<BulkSupportedRow> | null>(null);
  const [result, setResult] = useState<BulkCreateResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const { mutateAsync: bulkCreate, isPending } = usePostAuthApi<
    BulkCreateResult,
    BulkCreateSupportedsPayload
  >(postUrl);

  const handleFileSelected = async (selectedFile: File) => {
    setFile(selectedFile);
    setParseError(null);
    try {
      const parsed = await parseSupportedsExcel(selectedFile, await loadBankDirectory());
      if (parsed.rows.length === 0 && parsed.errors.length === 0) {
        setParseError(t('bulkUploads.noDataInFile'));
        return;
      }
      if (maxRows != null && parsed.rows.length > maxRows) {
        setParseError(t('bulkUploads.tooManyRows', { max: maxRows }));
        return;
      }
      setParseResult(parsed);
      setStep('confirm');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : t('bulkUploads.parseError'));
    }
  };

  const handleConfirm = async () => {
    if (!parseResult || !targetFund) return;

    try {
      const response = await bulkCreate({
        fundId: targetFund.id,
        rows: parseResult.rows,
      });
      setResult(response);
      setStep('results');
      onUploaded?.();
    } catch {
      setParseError(t('bulkUploads.serverError'));
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParseResult(null);
    setResult(null);
    setParseError(null);
  };

  return (
    <div>
      {file && step !== 'upload' && (
        <div className={styles.fileInfo}>
          <span className={`${styles.fileInfoName} body-md-semibold`}>{file.name}</span>
          <Button variant="outline" onClick={handleReset} disabled={isPending}>
            {t('bulkUploads.chooseAnother')}
          </Button>
        </div>
      )}

      {step === 'upload' && !targetFund && (
        <p className={`body-md-regular ${styles.selectFundHint}`}>{t('bulkUploads.selectFundFirst')}</p>
      )}

      {step === 'upload' && targetFund && (
        <>
          <FileUploadArea
            onFileSelected={handleFileSelected}
            templateApiUrl={templateUrl}
            templateFileName="bulk-supporteds-template.xlsx"
          />
          {parseError && <p className={`body-md-regular ${styles.errorText}`}>{parseError}</p>}
        </>
      )}

      {step === 'confirm' && parseResult && targetFund && (
        <ConfirmDialog
          fundName={targetFund.name}
          fundId={targetFund.id}
          rowCount={parseResult.rows.length}
          parseErrors={parseResult.errors}
          onConfirm={handleConfirm}
          onCancel={handleReset}
          isLoading={isPending}
          featureLabel={t('bulkUploads.createSupporteds')}
        />
      )}

      {step === 'results' && result && (
        <>
          <ResultsTable result={result} exportFileName="bulk-supporteds-errors.xlsx" />
          <div className={styles.centerActions}>
            <Button variant="outline" onClick={handleReset}>
              {t('bulkUploads.uploadAnother')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
